import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Actions,
  getMultiMonthFinancialStats,
} from "../../../actions/admin.action";
import Alert from "../../../components/alert";
import { MultiMonthFinancialStats } from "../../../dto";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import { ForbiddenError } from "../../../middleware";
import { ReduxState } from "../../../reducers";
import { isLoadingActive } from "../../../utils";
import InfoCardList from "../infoCardList";
import { ReportSectionAuthStatus } from "../types";
import { fetchDataForSelectedMonth, getMultiMonthLoanSection } from "../utils";

interface MultiMonthLoanStatsProps {
  selectedDate: Date | null;
  onAuthorizationChange: ReportSectionAuthStatus;
}

export default function MultiMonthLoanStats({
  selectedDate,
  onAuthorizationChange,
}: MultiMonthLoanStatsProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.admin.loadingQueue
  );
  const multiMonthFinancialStats: MultiMonthFinancialStats | null = useSelector(
    (state: ReduxState) => state.admin.multiMonthFinancialStats
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [notAuthorized, setNotAuthorized] = useState(false);

  const isLoading = isLoadingActive(loadingQueue, [
    Actions.REQUEST_GET_MULTI_MONTH_FINANCIAL_STATS,
  ]);

  useEffect(() => {
    if (!selectedDate) return;
    fetchDataForSelectedMonth(
      getMultiMonthFinancialStats,
      storeDispatch,
      selectedDate
    )
      .then(() => onAuthorizationChange("multiMonth", "authorized"))
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setNotAuthorized(true);
          onAuthorizationChange("multiMonth", "forbidden");
        } else {
          onAuthorizationChange("multiMonth", "error");
        }
      });
  }, [selectedDate, storeDispatch, onAuthorizationChange]);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center dashboard-container p-3">
        <Loader />
      </div>
    );
  }

  if (notAuthorized)
    return (
      <div className="dashboard-container">
        <Alert
          type="danger"
          message="Not authorized to view multi month loan stats."
        />
      </div>
    );

  if (!multiMonthFinancialStats)
    return (
      <div className="d-flex align-items-baseline justify-content-center">
        <p className="text-muted">
          Unable to load summary stats. Please reload the page again.
        </p>
      </div>
    );

  const { infoCards, sectionName } = getMultiMonthLoanSection(
    multiMonthFinancialStats
  );

  return (
    <div className="dashboard-container">
      <Alert
        message={errorMessage}
        type="info"
        canDismiss
        onDismissClick={() => setErrorMessage("")}
      />

      <section>
        <h3 className="user-page-title">{sectionName}</h3>
        <InfoCardList infoCards={infoCards} />
      </section>
    </div>
  );
}
